import { useOutletContext } from "react-router";
import type { SessionUser } from "../../lib/session-loader";
import { useT } from "../../lib/use-t";
import { daysUntilExam } from "../../lib/exam-date";
import { CountChip } from "../../components/vandaag/CountChip";
import { DagtaakHero } from "../../components/vandaag/DagtaakHero";
import { VoortgangCard } from "../../components/vandaag/VoortgangCard";
import { WoordenVandaagCard } from "../../components/vandaag/WoordenVandaagCard";
import { TipVanDeDag } from "../../components/vandaag/TipVanDeDag";

function greetingKey(hour: number): string {
  if (hour < 12) return "vandaag.greetingMorning";
  if (hour < 18) return "vandaag.greetingAfternoon";
  return "vandaag.greetingEvening";
}

export function VandaagPage() {
  const s = useOutletContext<SessionUser>();
  const t = useT();
  const now = new Date();
  const name = s.displayName;

  // Same nl-NL formatting pattern as TrendStrip. Long form for the desktop
  // eyebrow, short form for mobile.
  const dateLong = now.toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const dateShort = now.toLocaleDateString("nl-NL", {
    weekday: "short",
    day: "numeric",
    month: "long",
  });

  const days = daysUntilExam(now);

  return (
    <div className="mx-auto flex max-w-[1080px] flex-col gap-[18px] lg:gap-6">
      {/* Topline: greeting block + (when a future exam date is set) countdown chip. */}
      <div className="flex items-end justify-between gap-5">
        <div className="min-w-0">
          <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
            <span className="lg:hidden">{dateShort}</span>
            <span className="hidden lg:inline">{dateLong}</span>
          </p>
          <h1 className="mt-1 text-[25px] font-semibold tracking-tight lg:text-[34px]">
            {t(greetingKey(now.getHours()), { name })}
          </h1>
          <p className="mt-1.5 text-sm text-[var(--ink-muted)] lg:text-[15px]">
            {t("vandaag.subline")}
          </p>
        </div>
        {days !== null ? (
          <div className="hidden flex-none lg:block">
            <CountChip days={days} />
          </div>
        ) : null}
      </div>

      {/* Mobile countdown chip sits below the greeting block in the flow. */}
      {days !== null ? (
        <div className="lg:hidden">
          <CountChip days={days} />
        </div>
      ) : null}

      {/* Grid: hero (wider) + side stack. Single column under lg. */}
      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[1.65fr_1fr] lg:gap-6">
        <DagtaakHero />
        <div className="flex flex-col gap-[18px] lg:gap-6">
          <VoortgangCard />
          <WoordenVandaagCard />
        </div>
      </div>

      <TipVanDeDag />
    </div>
  );
}
